import moment from 'moment';

import {
  LevelRewardInput,
  LevelRewardInputType,
  MarathonEconomyConfigInput,
  MarathonMilestoneGroupInput,
  MarathonMilestoneType,
  MMilestonesInput,
} from '../../../../../__gqltypes__';

// Editor-side (runtime) shapes: query results stripped of __typename so they can
// be sent back as inputs untouched.
// `_clientId` is a stable, editor-only identity: it gives React a key that
// follows a row when siblings are removed/reordered (so child EditValue focus,
// local state and React.memo stay attached to the right item). It lives only in
// editor state and is stripped from the mutation input by milestoneToInput /
// groupsToInput, which build fresh objects with explicit fields.
export type MMilestone = {
  _clientId: string;
  numberOfGuesses: number;
  type: MarathonMilestoneType;
  basicReward: LevelRewardInput;
  plusReward: LevelRewardInput;
};

export type MGroup = {
  _clientId: string;
  unlockDate: Date;
  stopDate: Date;
  milestones: MMilestone[];
};

// Monotonic, session-scoped counter — unique within the editor's lifetime, which
// is all a React sibling key needs. Assigned at create/load time (never during
// render), so each item's id is stable across re-renders.
let clientIdCounter = 0;
export function nextClientId(): string {
  clientIdCounter += 1;
  return `c${clientIdCounter}`;
}

export type MEconomy = {
  startPrice: number;
  revivePrice: number;
  freeStartsPlus: number;
  freeStartsBasic: number;
  freeRevivesPlus: number;
  freeRevivesBasic: number;
};

// Structural shapes of the query results we convert from (with or without __typename)
type MilestoneConfigShape = {
  numberOfGuesses: number;
  type: MarathonMilestoneType;
  basicReward: { type: LevelRewardInputType; amount: number; itemId?: string | null };
  plusReward: { type: LevelRewardInputType; amount: number; itemId?: string | null };
};

// The Date scalar arrives as epoch milliseconds; normalize to Date for the pickers.
type GroupConfigShape = {
  unlockDate: Date | string | number;
  stopDate: Date | string | number;
  milestones: MilestoneConfigShape[];
};

// Minimum app version gated onto Marathon Phase-1 events so pre-release clients (which can't parse
// the new milestone-group / type / economy shape) never receive the event. The backend pre-fills
// this into extraConfig.filters.appVersion for NEW events (createMarathonEvent) but does NOT
// normalize it onto OLD pre-existing rows, so the editor defaults it client-side when an event has
// no app-version filter — see MarathonEventData. MUST mirror SP3BE MARATHON_IMPROVEMENT_MIN_APP_VERSION;
// keep the two in sync.
export const MARATHON_MIN_APP_VERSION = '003.013.000';

export const MARATHON_ECONOMY_DEFAULTS: MEconomy = {
  startPrice: 20,
  revivePrice: 25,
  freeStartsPlus: 1,
  freeStartsBasic: 1,
  freeRevivesPlus: 1,
  freeRevivesBasic: 1,
};

export const ECONOMY_FIELDS: Array<{ key: keyof MEconomy; label: string }> = [
  { key: 'freeStartsPlus', label: 'Free starts — PLUS User (per event)' },
  { key: 'freeStartsBasic', label: 'Ad free starts — BASIC User (per event)' },
  { key: 'freeRevivesPlus', label: 'Free revives — PLUS User (per round)' },
  { key: 'freeRevivesBasic', label: 'Ad free revives — BASIC User (per round)' },
  { key: 'startPrice', label: 'Diamond start price' },
  { key: 'revivePrice', label: 'Diamond revive price' },
];

export const MILESTONE_TYPE_OPTIONS: Array<{ value: MarathonMilestoneType; label: string; badgeVariant: string }> = [
  { value: MarathonMilestoneType.REGULAR, label: 'Regular', badgeVariant: 'secondary' },
  { value: MarathonMilestoneType.REST_POINT, label: 'Rest point', badgeVariant: 'info' },
  { value: MarathonMilestoneType.CHECKPOINT, label: 'Checkpoint', badgeVariant: 'warning' },
  { value: MarathonMilestoneType.COMBINED, label: 'Combined', badgeVariant: 'primary' },
];

export function getMilestoneTypeOption(type: MarathonMilestoneType) {
  return MILESTONE_TYPE_OPTIONS.find((o) => o.value === type) ?? MILESTONE_TYPE_OPTIONS[0];
}

const MILESTONE_DEFAULT_STEP = 10;

function rewardToRuntime(reward: MilestoneConfigShape['basicReward']): LevelRewardInput {
  return {
    type: reward.type,
    amount: reward.amount,
    itemId: reward.itemId,
  };
}

export function milestoneConfigToRuntime(miles: readonly MilestoneConfigShape[]): MMilestone[] {
  return miles.map((mile) => ({
    _clientId: nextClientId(),
    numberOfGuesses: mile.numberOfGuesses,
    type: mile.type,
    basicReward: rewardToRuntime(mile.basicReward),
    plusReward: rewardToRuntime(mile.plusReward),
  }));
}

export function groupConfigToRuntime(groups: readonly GroupConfigShape[]): MGroup[] {
  return groups.map((group) => ({
    _clientId: nextClientId(),
    unlockDate: new Date(group.unlockDate),
    stopDate: new Date(group.stopDate),
    milestones: milestoneConfigToRuntime(group.milestones),
  }));
}

export function economyConfigToRuntime(economy: MEconomy): MEconomy {
  return {
    startPrice: economy.startPrice,
    revivePrice: economy.revivePrice,
    freeStartsPlus: economy.freeStartsPlus,
    freeStartsBasic: economy.freeStartsBasic,
    freeRevivesPlus: economy.freeRevivesPlus,
    freeRevivesBasic: economy.freeRevivesBasic,
  };
}

export function newMilestone(numberOfGuesses: number): MMilestone {
  return {
    _clientId: nextClientId(),
    numberOfGuesses,
    type: MarathonMilestoneType.REGULAR,
    basicReward: { type: LevelRewardInputType.CURRENCY_COIN, amount: 10 },
    plusReward: { type: LevelRewardInputType.CURRENCY_COIN, amount: 10 },
  };
}

export function getMaxMilestoneValue(groups: readonly MGroup[], untilGroupIndex?: number): number {
  const end = untilGroupIndex ?? groups.length;
  return groups
    .slice(0, end)
    .reduce((max, group) => group.milestones.reduce((m, mile) => Math.max(m, mile.numberOfGuesses), max), 0);
}

export function nextMilestoneValue(groups: readonly MGroup[], groupIndex: number): number {
  const groupMax = getMaxMilestoneValue(groups, groupIndex + 1);
  return groupMax + MILESTONE_DEFAULT_STEP;
}

// Highest numberOfGuesses inside a single group (null for an empty group). The matching milestone
// is the group boundary and is always COMBINED (checkpoint + rest point) — auto-set, not editable.
export function groupMaxValue(milestones: readonly MMilestone[]): number | null {
  if (milestones.length === 0) {
    return null;
  }
  return milestones.reduce((max, mile) => Math.max(max, mile.numberOfGuesses), milestones[0].numberOfGuesses);
}

export function isGroupMaxMilestone(milestones: readonly MMilestone[], index: number): boolean {
  const max = groupMaxValue(milestones);
  return max !== null && milestones[index]?.numberOfGuesses === max;
}

// New group starts where the event currently ends and gets a first milestone above
// everything configured so far.
export function newGroup(existing: readonly MGroup[]): MGroup {
  const lastGroup = existing[existing.length - 1];
  const unlockDate = lastGroup ? new Date(lastGroup.stopDate) : moment().add(1, 'hour').startOf('hour').toDate();
  const stopDate = moment(unlockDate).add(7, 'days').toDate();
  return {
    _clientId: nextClientId(),
    unlockDate,
    stopDate,
    milestones: [newMilestone(getMaxMilestoneValue(existing) + MILESTONE_DEFAULT_STEP)],
  };
}

export function milestoneToInput(mile: MMilestone): MMilestonesInput {
  return {
    numberOfGuesses: mile.numberOfGuesses,
    type: mile.type,
    basicReward: { ...mile.basicReward },
    plusReward: { ...mile.plusReward },
  };
}

// IMPORTANT: a non-last group's stopDate is NOT an availability gate. The backend gates milestone
// availability only on group.unlockDate (open-ended) and the EVENT-level stop (= last group's
// stopDate); see SP3BE getUnlockedCeiling / claimMarathonMilestone. So earlier groups never close
// when a later group unlocks. We still set a non-last group's stopDate = the next group's unlock for
// schedule contiguity — it is harmless/vestigial server-side and must not be surfaced in the UI as a
// per-group "close" date. Only the last group's stopDate (the event end) is meaningful.
export function groupsToInput(groups: readonly MGroup[]): MarathonMilestoneGroupInput[] {
  return groups.map((group, index) => {
    const maxValue = groupMaxValue(group.milestones);
    return {
      unlockDate: group.unlockDate,
      stopDate: index < groups.length - 1 ? groups[index + 1].unlockDate : group.stopDate,
      // coerce the group-max milestone to COMBINED on save (mirrors the backend normalize/upgrade)
      milestones: group.milestones.map((mile) => {
        const input = milestoneToInput(mile);
        return mile.numberOfGuesses === maxValue ? { ...input, type: MarathonMilestoneType.COMBINED } : input;
      }),
    };
  });
}

export function economyToInput(economy: MEconomy): MarathonEconomyConfigInput {
  return economyConfigToRuntime(economy);
}

// Per-row inline errors; prevMax is the highest milestone value of all previous groups.
export function getMilestoneRowErrors(milestones: readonly MMilestone[], prevMax: number): Array<string | null> {
  let prev = prevMax;
  return milestones.map((mile) => {
    let error: string | null = null;
    if (!Number.isInteger(mile.numberOfGuesses) || mile.numberOfGuesses <= 0) {
      error = 'Must be a positive integer';
    } else if (mile.numberOfGuesses <= prev) {
      error = `Must be greater than the previous milestone (${prev})`;
    }
    prev = Math.max(prev, mile.numberOfGuesses);
    return error;
  });
}

export function formatEventDate(date: Date | string | number): string {
  return moment(date).format('YYYY-MM-DD HH:mm');
}

export function relativeDateHint(date: Date | string | number): string {
  return moment(date).fromNow();
}

// Absolute duration between two dates as "X Days X Hours X Minutes" (all units always shown).
export function formatEventDuration(
  start: Date | string | number,
  stop: Date | string | number
): string {
  let totalMinutes = Math.max(0, moment(stop).diff(moment(start), 'minutes'));
  const days = Math.floor(totalMinutes / (60 * 24));
  totalMinutes -= days * 60 * 24;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  return `${days} Days ${hours} Hours ${minutes} Minutes`;
}

// Minute-precise relative descriptor: "in 3 days 21 hours 19 minutes" / "1 hour ago".
// Unlike moment().fromNow() (which rounds to a single coarse unit), this keeps days + hours +
// minutes and drops zero-valued units.
export function preciseRelativeHint(date: Date | string | number): string {
  const target = moment(date);
  const now = moment();
  const isPast = target.isBefore(now);
  let totalMinutes = Math.abs(target.diff(now, 'minutes'));
  const days = Math.floor(totalMinutes / (60 * 24));
  totalMinutes -= days * 60 * 24;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days} day${days === 1 ? '' : 's'}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) {
    parts.push('0 minutes');
  }
  const duration = parts.join(' ');
  return isPast ? `${duration} ago` : `in ${duration}`;
}

// A CONTENT_PACK_REWARD carries its content pack id in `itemId` (a free-text field in
// EditRewardsModal). The id can be left blank, which saves a malformed reward that later breaks reads
// (it contributed to a login failure), so treat an empty/whitespace-only id as invalid.
function isContentPackRewardMissingId(reward: LevelRewardInput): boolean {
  return reward.type === LevelRewardInputType.CONTENT_PACK_REWARD && (reward.itemId ?? '').trim() === '';
}

export function validateMarathonEvent(groups: readonly MGroup[], economy: MEconomy): string[] {
  const errors: string[] = [];
  if (groups.length === 0) {
    errors.push('At least one milestone group is required.');
  }
  let prevMax = 0;
  groups.forEach((group, index) => {
    if (group.milestones.length === 0) {
      errors.push(`Group ${index + 1}: at least one milestone is required.`);
    }
    getMilestoneRowErrors(group.milestones, prevMax).forEach((error, mIndex) => {
      if (error) {
        errors.push(`Group ${index + 1}, milestone ${mIndex + 1}: ${error.toLowerCase()}.`);
      }
    });
    // Validate both reward slots of every milestone: a ContentPack reward must have a content pack id.
    group.milestones.forEach((mile) => {
      ([
        { reward: mile.basicReward, slot: 'BASIC' },
        { reward: mile.plusReward, slot: 'PLUS' },
      ] as const).forEach(({ reward, slot }) => {
        if (isContentPackRewardMissingId(reward)) {
          errors.push(`Group ${index + 1} · milestone ${mile.numberOfGuesses} · ${slot} reward: select a Content Pack.`);
        }
      });
    });
    prevMax = group.milestones.reduce((max, mile) => Math.max(max, mile.numberOfGuesses), prevMax);
    if (index > 0 && !moment(group.unlockDate).isAfter(moment(groups[index - 1].unlockDate))) {
      errors.push(`Group ${index + 1}: unlock date must be after group ${index}'s unlock date.`);
    }
  });
  const lastGroup = groups[groups.length - 1];
  if (lastGroup && !moment(lastGroup.stopDate).isAfter(moment(lastGroup.unlockDate))) {
    errors.push("Event stop date must be after the last group's unlock date.");
  }
  ECONOMY_FIELDS.forEach(({ key, label }) => {
    const value = economy[key];
    if (!Number.isInteger(value) || value < 0) {
      errors.push(`Economy: "${label}" must be an integer ≥ 0.`);
    }
  });
  return errors;
}

// ---------------------------------------------------------------------------------------------------------------------
// Live-event edit safety: detect which risky categories changed vs the originally loaded event, so the
// editor can warn before saving an event that players are mid-way through.
// ---------------------------------------------------------------------------------------------------------------------

export type LiveEditRisk = 'HIGH' | 'MEDIUM' | 'LOW';

// Everything needed to diff an edit, in the editor's runtime shapes.
export type MarathonEditSnapshot = {
  name: string;
  category: string;
  groups: MGroup[];
  economy: MEconomy;
  // JSON signature of the audience matchers (incl. min app version)
  matcherSignature: string;
};

const epoch = (date: Date | string | number): number => new Date(date).getTime();

const rewardSig = (reward: LevelRewardInput): string => `${reward.type}:${reward.amount}:${reward.itemId ?? ''}`;

const scheduleSignature = (groups: readonly MGroup[]): string =>
  `${groups.length}#${groups.map((g) => `${epoch(g.unlockDate)}-${epoch(g.stopDate)}`).join('|')}`;

const structureSignature = (groups: readonly MGroup[]): string =>
  groups.map((g) => g.milestones.map((m) => m.numberOfGuesses).join(',')).join('|');

const typesSignature = (groups: readonly MGroup[]): string =>
  groups.map((g) => g.milestones.map((m) => m.type).join(',')).join('|');

const rewardsSignature = (groups: readonly MGroup[]): string =>
  groups.map((g) => g.milestones.map((m) => `${rewardSig(m.basicReward)}/${rewardSig(m.plusReward)}`).join(',')).join('|');

const economySignature = (economy: MEconomy): string => ECONOMY_FIELDS.map(({ key }) => `${key}=${economy[key]}`).join(',');

export type DetectedLiveChanges = { high: string[]; medium: string[]; low: string[] };

// Compares the originally loaded event with the current editor state and groups the differences by
// risk. HIGH = anything that can corrupt in-flight player progress or already-claimed rewards.
export function detectLiveEventChanges(before: MarathonEditSnapshot, after: MarathonEditSnapshot): DetectedLiveChanges {
  const high: string[] = [];
  const medium: string[] = [];
  const low: string[] = [];

  if (scheduleSignature(before.groups) !== scheduleSignature(after.groups)) {
    high.push('group schedule (groups added/removed or unlock/stop dates changed)');
  }
  if (structureSignature(before.groups) !== structureSignature(after.groups)) {
    high.push('milestone structure (milestones added/removed/reordered or song counts changed)');
  }
  if (typesSignature(before.groups) !== typesSignature(after.groups)) {
    high.push('milestone types');
  }
  if (rewardsSignature(before.groups) !== rewardsSignature(after.groups)) {
    high.push('milestone rewards');
  }
  if (economySignature(before.economy) !== economySignature(after.economy)) {
    medium.push('economy config (prices / free starts / free revives)');
  }
  if (before.name !== after.name) {
    low.push('event name');
  }
  if (before.category !== after.category) {
    low.push('category');
  }
  if (before.matcherSignature !== after.matcherSignature) {
    low.push('audience matchers / min app version');
  }

  return { high, medium, low };
}

export function hasLiveChanges(changes: DetectedLiveChanges): boolean {
  return changes.high.length > 0 || changes.medium.length > 0 || changes.low.length > 0;
}

export function highestChangeRisk(changes: DetectedLiveChanges): LiveEditRisk | null {
  if (changes.high.length > 0) {
    return 'HIGH';
  }
  if (changes.medium.length > 0) {
    return 'MEDIUM';
  }
  if (changes.low.length > 0) {
    return 'LOW';
  }
  return null;
}
